import Card from "../shared/cards/Card";
import { swatch, downloadIcon } from "../../assets/components/svgs";
import { useTranslation } from "react-i18next";
import { Label } from "../../styles/StyledComponents";
import InputField from "../shared/Fields/InputField";
import { getPropertyNames, updateThemeFile } from "./Util";
import CollapsibleCard from "./CollapsibleCard";
import { themeSchema } from "../../constants/ThemeConstants";
import InfoPopover from "../shared/Fields/InfoPopover";
import MenuButtonsPortal from "../Menu/MenuButtonsPortal";
import BiButton from "../primitives/buttons/BiButton.primitive";
import { useTheme } from "../../context/themeContext";
import { useState } from "react";
import { isEqual } from "lodash";
import { updateTheme } from "../../context/Themes";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setToastData } from "../../app/toastReducer";
import { HTTP_STATUS } from "../../http/constants/http.status";
import LoaderContainer from "../shared/loaderContainer/LoaderContainer";

interface ThemeProps {}

/**
 * @parentId customization
 * @manager Theme Styling
 * @overview Manage the Overall Look and Feel of the Portal.
 *
 * <section>
 *   <p>
 *     The second tab under Customization is the Theme Styling which is used to manage the look and feel of the entire portal.
 *     Using this feature an Admin User can change the colour scheme of the portal. It has provision for the following:
 *   </p>
 *   <p>
 *     <table>
 *       <tr>
 *         <th>Name</th>
 *         <th>Description</th>
 *       </tr>
 *       <tr>
 *         <td>Colour Palette</td>
 *         <td>Defines Main Colour schemes of the entire portal</td>
 *       </tr>
 *       <tr>
 *         <td>Disabled Colours</td>
 *         <td>Defines Colours of disabled elements</td>
 *       </tr>
 *       <tr>
 *         <td>Hover Colours</td>
 *         <td>Defines Colours when elements are hovered</td>
 *       </tr>
 *       <tr>
 *         <td>Text Colours</td>
 *         <td>Defines Colours of all text strings</td>
 *       </tr>
 *     </table>
 *   </p>
 *   <h3>Colour Palette</h3>
 *   <ul>
 *     <li>
 *       <h4>Primary Colour Choice</h4>
 *       <p>This applies to all primary buttons, sign-out icons, and other primary theme elements.</p>
 *     </li>
 *     <li>
 *       <h4>Secondary Colour Choice 1</h4>
 *       <p>This applies to the menu background colour, selected menu icons colour, page title and other secondary theme elements.</p>
 *     </li>
 *     <li>
 *       <h4>Secondary Colour Choice 2</h4>
 *       <p>This applies to colours of section headers, dropdown items and page background.</p>
 *     </li>
 *   </ul>
 *   </section>
 *   <section>
 *     <h3>Disabled Colours</h3>
 *     <ul>
 *       <li>
 *         <h4>Primary Disabled Colour Choice</h4>
 *         <p>This colour applies to all elements which are disabled under the primary theme.</p>
 *       </li>
 *       <li>
 *         <h4>Secondary Disabled Colour Choice</h4>
 *         <p>This colour applies to all elements which are disabled under the secondary theme.</p>
 *       </li>
 *     </ul>
 *   </section>
 *   <section>
 *     <h3>Hover Colours</h3>
 *     <ul>
 *       <li>
 *         <h4>Primary Hover Colour Choice</h4>
 *         <p>This colour applies to all elements which are hovered under the primary theme.</p>
 *       </li>
 *       <li>
 *         <h4>Secondary Hover Colour Choice</h4>
 *         <p>This colour applies to all elements which are hovered under the secondary theme.</p>
 *       </li>
 *     </ul>
 *   </section>
 *   <section>
 *     <h3>Text Colours</h3>
 *     <ul>
 *       <li>
 *         <h4>Primary Text Colour Choice</h4>
 *         <p>This colour applies to all text elements under the primary theme.</p>
 *       </li>
 *       <li>
 *         <h4>Secondary Text Colour Choice</h4>
 *         <p>This colour applies to all text elements under the secondary theme.</p>
 *       </li>
 *     </ul>
 *   </section>
 *  <section>
 *   <h3>Functionality</h3>
 *   <p>
 *     The theme data is stored in a theme.json file on the Google Cloud Bucket.
 *     On the initial launch of the portal this JSON file is read and the theme is then applied to the application.
 *     <br>
 *     If the file is not found on the Google Cloud Bucket then default theme is applied which is maintained locally on the application and if Admin user makes a change and clicks on <strong>Save</strong> Button then a theme.json file is created and uploaded on the server.
 *     <br> <br>
 *     Admin User can either enter the HEX colour code or use a colour picker (by clicking on the colour block).
 *     Once a change in any of the colours is done the <strong>Save</strong> button gets enabled.
 *     Clicking on the Save button calls the AMS-05 Create Signed URL which on success calls the Upload GCP File PUT Call and updates the theme.json file.
 *     Once the theme.json file is updated the changes are reflected immediately without the need of explicit refreshing.
 *   </p>
 * </section>
 * </section>
 * <section>
 *   <h4>Failure Status Codes</h4>
 *   <p>
 *     This section describes the status codes for failures related to the Zendesk User Requests.
 *     <br>
 *     <table>
 *       <tr>
 *         <th>HTTP Status Code</th>
 *         <th>Service Error Code</th>
 *         <th>Error Message</th>
 *       </tr>
 *       <tr>
 *         <td>401</td>
 *         <td>UMS_AUTH_REQUIRED</td>
 *         <td>You must authenticate before you can use user-management-service.</td>
 *       </tr>
 *       <tr>
 *         <td>500</td>
 *         <td>NA</td>
 *         <td>Internal Server Error</td>
 *       </tr>
 *     </table>
 *   </p>
 * </section>
 * <section>
 *   <h5>Platform Service(s)</h5>
 *   <p>Table lists all the dependent platform service(s) with specific version(s) for Create/View request page.</p>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Service Name</th>
 *       <th>Version</th>
 *     </tr>
 *     <tr>
 *       <td>Asset Management Service</td>
 *       <td>1.3.0</td>
 *     </tr>
 *     <tr>
 *       <td>User Management Service</td>
 *       <td>1.2.2</td>
 *     </tr>
 *   </table>
 *   <br>
 *   <table>
 *     <tr>
 *       <th>Feature</th>
 *       <th>API URL</th>
 *       <th>API Method</th>
 *       <th>API Permission</th>
 *       <th>Required</th>
 *     </tr>
 *     <tr>
 *       <td>Create Signed URL</td>
 *       <td>/signed-url</td>
 *       <td>POST</td>
 *       <td>asset-management-service.signed-url.create</td>
 *       <td>Yes</td>
 *     </tr>
 *   </table>
 * </section>
 */

const ThemeStyling = ({}: ThemeProps) => {
  const { t } = useTranslation();
  const { rawTheme: theme, setRawTheme, setTheme } = useTheme();

  const [themeColors, setThemeColors] = useState(theme);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAppDispatch();

  const userData = useAppSelector(
    (state: any) => state.userManagement.userProfileInfo
  );

  const handleColorChange = (colorKey: string, newColor: string) => {
    setThemeColors((prevColors: any) => ({
      ...prevColors,
      [colorKey]: newColor,
    }));
  };

  const discardTheme = () => {
    setThemeColors(theme);
  };

  const themeCallback = () => {
    const {
      primary,
      primaryDisabled,
      primaryHover,
      primaryTextColor,
      secondary1,
      secondary1TextColor,
      secondary2,
      secondaryDisabled,
      secondaryHover,
    } = themeColors;
    const updatedTheme = updateTheme(
      primary,
      primaryDisabled,
      primaryHover,
      primaryTextColor,
      secondary1,
      secondary1TextColor,
      secondary2,
      secondaryDisabled,
      secondaryHover
    );
    setRawTheme(themeColors);
    setTheme(updatedTheme);
  };

  const savePayload = async () => {
    let themeFlag = !isEqual(themeColors, theme);

    function errorHandling(errorText: string) {
      setIsLoading(false);
      dispatch(
        setToastData({
          toastMessage: errorText,
          isToastActive: true,
          toastType: "error",
        })
      );
    }

    if (themeFlag) {
      setIsLoading(true);
      let response: any = await updateThemeFile(
        JSON.stringify(themeColors, null, "\t"),
        userData ? userData.userId : ""
      );
      setIsLoading(false);
      if (HTTP_STATUS.isSuccess(response?.status)) {
        dispatch(
          setToastData({
            toastMessage: t("T_STYLING_UPDATE_SUCCESS"),
            isToastActive: true,
            toastType: "success",
          })
        );
        themeCallback();
      } else {
        errorHandling(t("T_ERROR_SOMETHING_WRONG"));
      }
    }
  };

  return (
    <LoaderContainer isLoading={isLoading}>
      <div className="p-4">
        <MenuButtonsPortal>
          <BiButton
            className={"tertiary mr-2 border py-1.5"}
            type="button"
            onClick={discardTheme}
          >
            {t("T_CANCEL")}
          </BiButton>

          <BiButton
            className={"bg-primary py-1.5 text-white"}
            type="button"
            disabled={isEqual(themeColors, theme)}
            onClick={savePayload}
          >
            {t("T_SAVE")}
          </BiButton>
        </MenuButtonsPortal>
        <Card
          title={t("T_CUSTOMIZATION_THEME")}
          icon={swatch}
          alt="Clip Icon"
          height=""
          width=""
          className="w-3/4"
        >
          {Object.entries(themeSchema).map(
            ([type, attributes]: any, index: number) => (
              <CollapsibleCard
                label={attributes.label}
                key={type}
                index={index}
              >
                <div className="grid grid-cols-3 gap-4 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3">
                  {Object.entries(attributes.value).map(
                    ([colorKey, colorAttributes]: any) => (
                      <div key={colorKey} className="mb-4 flex flex-col">
                        <div className="flex flex-row">
                          <Label>{t(colorAttributes.label)}</Label>{" "}
                          <InfoPopover
                            className="z-10 ml-2"
                            content={t(colorAttributes.description)}
                          ></InfoPopover>
                        </div>
                        <div className="flex flex-row">
                          <input
                            className="mt-2 cursor-pointer"
                            data-testid={`color-picker-${colorKey}`}
                            type="color"
                            id={`style-color-${colorKey}`}
                            name={`style-color-${colorKey}`}
                            value={themeColors[colorKey]}
                            onChange={(e) =>
                              handleColorChange(colorKey, e.target.value)
                            }
                          ></input>
                          <div className="ml-2 flex flex-col">
                            <InputField
                              type="text"
                              id={`color-hex-${colorKey}`}
                              label={""}
                              className="w-32"
                              min={4}
                              max={7}
                              placeholder={"Input Hex Code"}
                              required={true}
                              disableLabel
                              errorClass="text-sm ml-2"
                              disabled={false}
                              onChange={(value: any) =>
                                handleColorChange(colorKey, value)
                              }
                              value={themeColors[colorKey]}
                              error=""
                            />
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              </CollapsibleCard>
            )
          )}
        </Card>
      </div>
    </LoaderContainer>
  );
};

export default ThemeStyling;
